import { SupportChat } from 'src/app/core/util/support-chat';
import { Injectable } from '@angular/core';
import { inProcessingRecord } from 'src/app/modules/bookkeeping/data/in-processing-record';
import { Invoice } from 'src/app/modules/bookkeeping/models/invoice/invoice';
import { RecognizedInvoiceService } from 'src/app/modules/bookkeeping/services/recognized-invoice.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ToasterTypes } from '../../enum/toaster-type.enum';
import { HubNotification } from '../../model/notification';
import { BaseHubService } from './base-hub.service';
import { HubFactoryService } from './hub-factory.service';
import * as $ from "jquery";
import { DomainName } from '../../../data/enums/accounting-integration';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { WorkflowRunPanelService } from 'src/app/data/service/workflow-run-panel.service';
import { IncomingEmailService } from 'src/app/data/service/incoming-email.service';
import { TasksService } from 'src/app/data/service/tasks.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsHubService extends BaseHubService {
  protected readonly hubUrl = 'hub/notifications';

  constructor(
    hubFactory: HubFactoryService,
    toasterMessageService: ToasterMessageService,
    private readonly _recognizedInvoiceService: RecognizedInvoiceService,
    private readonly _invoiceService: InvoiceService,
    private readonly _runPanelService: WorkflowRunPanelService,
    private readonly _incomingEmailService: IncomingEmailService,
    private readonly _tasksService: TasksService,
    public organisationService: OrganizationService
  ) {
    super(hubFactory, toasterMessageService);
  }

  protected onStart(): void | Promise<void> {
    this.hubConnection.on('SendNotification', (data: HubNotification) => {
      switch (data?.eventType) {
        case 'InvoicePublished':
          this.publishedInvoiceNotification(data);
          break;
        case 'InvoiceInProcessing':
          this.invoiceProcessingNotification(data);
          break;
        case 'InvoiceProcessed':
          this.invoiceProcessedNotification(data);
          break;
        case 'ExpensePublished':
          this.expensePublishedNotification(data);
          break;
        case 'InvoiceLimitExceed':
          this.invoiceLimitExceedNotification(data);
          break;
        case 'ErrorOnInvoicePublish':
          this.handleErrorOnInvoicePublish(data);
          break;
        case 'WorkflowRunUpdated':
          this.workflowRunUpdated(data);
          break;
        case 'IncomingEmailReceived':
          this.incomingEmailReceived(data);
          break;
        case 'TaskChanged':
          this.taskChanged(data);
          break;
        // A team reply in the user's support chat: it reads it now rather than at its next poll.
        case 'SupportReply':
          SupportChat.notifyReply();
          break;
      }
    });
  }

  /** SD-1151: a workflow run's step started or finished. Republished for the History side panel
   *  rather than toasted — this fires several times per run and is a data update, not news. */
  private workflowRunUpdated(data: HubNotification) {
    const runId = data?.data?.runId || data?.data?.RunId;
    if (!runId) { return; }
    const status = data?.data?.status || data?.data?.Status;
    const kind = data?.data?.kind || data?.data?.Kind;
    this._runPanelService.notifyRunUpdated(String(runId), status, kind);
  }

  /** A task was raised, completed or reassigned. Republished for the Tasks card, which reloads;
   *  not toasted, because the person who acted is among those told. */
  private taskChanged(data: HubNotification) {
    const taskId = data?.data?.taskId || data?.data?.TaskId;
    if (!taskId) { return; }
    this._tasksService.notifyChanged({
      taskId: String(taskId),
      status: data?.data?.status || data?.data?.Status,
      organizationId: data.organizationId
    });
  }

  /** A message landed in a connected mailbox. Republished for the Emails page rather than
   *  toasted: the page adds the row itself, and a toast per arriving email would be the same
   *  news twice on a busy inbox. Not filtered by organisation here -- IncomingEmailService
   *  does that per subscriber, because the selected organisation can change while connected. */
  private incomingEmailReceived(data: HubNotification) {
    const payload = data?.data;
    if (!payload) { return; }
    this._incomingEmailService.notifyArrived({
      id: Number(payload.id ?? payload.Id ?? 0),
      organizationId: payload.organizationId ?? payload.OrganizationId ?? data?.organizationId,
      from: payload.from ?? payload.From,
      fromEmail: payload.fromEmail ?? payload.FromEmail,
      subject: payload.subject ?? payload.Subject,
      toEmail: payload.toEmail ?? payload.ToEmail,
      submissionMethod: payload.submissionMethod ?? payload.SubmissionMethod,
      attachmentCount: payload.attachmentCount ?? payload.AttachmentCount,
      receivedAt: payload.receivedAt ?? payload.ReceivedAt,
      source: payload.source ?? payload.Source
    });
  }

  async stop() {
    if (this.hubConnection?.state == 'Connected') {
      this.hubConnection?.off('SendNotification');
      await this.hubConnection?.stop()
        .then(() => { console.log('Connection Stoped') });
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private publishedInvoiceNotification(data: HubNotification) {
    this._toasterMessageService.displayToasterState(ToasterTypes.SuccessWithHtmlAsync, null,
      `Bill ${data?.data} processed for payment. View <a  class=\"toastr-link\" href=\"/payable/outstanding-amounts\">Bills to Pay</a>`);
      this._invoiceService.refreshXeroData(localStorage.SelectedOrganization);
  }

  private invoiceProcessingNotification(data: HubNotification) {
    if (data?.organizationId == localStorage.SelectedOrganization) {
      this._recognizedInvoiceService.addInProcessingRecord(inProcessingRecord);
      this._toasterMessageService.displayToasterState(ToasterTypes.SuccessWithHtmlAsync, 'Processing', 'A new document is being processed');
    }
  }

  private invoiceProcessedNotification(data: HubNotification) {
    if (data?.organizationId == localStorage.SelectedOrganization) {
      let invoice = data?.data as Invoice;

      if (invoice) {
        this._toasterMessageService.displayToasterState(ToasterTypes.SuccessWithHtmlAsync, 'New Invoice Processed',
          `An invoice from ${invoice.supplierName} was just processed. <a  class=\"toastr-link\" href=\"/inbox/editInvoice/${invoice.id}\">View invoice</a>`);
        if ($(".card-2") != undefined) {
          $(".card-2 .number span").html(parseInt($(".card-2 .number span").html()) + 1);
        }
        if (!data.justMessageShow) {
          this._recognizedInvoiceService.send(invoice);
        }
      }
    }
  }
  private expensePublishedNotification(data: HubNotification) {
    if (data?.data?.accType == "QuickBooks") {
      let type = "";
      if (data?.data?.paymentType.toLowerCase() == "check") {
        type = "check"
      }
      else {
        type = "expense"
      }
      this.organisationService.getIntegrationCredentials('qbo').subscribe((res) => {
        if (res) {
          this.organisationService.refreshConnectionURL = res.revokeEndPoint;
          this._toasterMessageService.displayToasterState(ToasterTypes.SuccessWithHtmlAsync, null,
            `Receipt ${data?.data?.ref} sent to <a  class=\"toastr-link\" target=\"_blank\"
       href=\"${this.organisationService.baseURL}/${type}?txnId=${data?.data?.id}">QuickBooks</a>`);
        }
      });
    }
    else {
      this._toasterMessageService.displayToasterState(ToasterTypes.SuccessWithHtmlAsync, null,
        `Receipt ${data?.data?.ref} sent to <a  class=\"toastr-link\" target=\"_blank\"
       href=\"https://go.xero.com/organisationlogin/default.aspx?shortcode=${data?.data?.orgCode}&redirecturl=/Bank/ViewTransaction.aspx?bankTransactionID=${data?.data?.id}">Xero</a>`);
    }

    if (data?.organizationId == localStorage.SelectedOrganization) {
      this._recognizedInvoiceService.notifyExpensePublished(data);
    }
  }
  private invoiceLimitExceedNotification(data: HubNotification) {
    this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, null,
      `You’ve exceeded your monthly document limit. Upgrade your subscription. <a  class=\"toastr-link\" href=\"/setting/billing\">Upgrade</a>`);
  }

  private handleErrorOnInvoicePublish(data: HubNotification) {

    if (data?.title == 'ConnectionLost') {
      if (data?.data?.type == DomainName.QuickBooks) {
        this.organisationService.getIntegrationCredentials('qbo').subscribe((res) => {
          if (res) {
            this.organisationService.refreshConnectionURL = res.revokeEndPoint;
            this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, null,
              `QuickBooks connection lost. Please <a  class=\"toastr-link\" href=\"${this.organisationService.refreshConnectionURL}\">refresh</a> connection`);
          }
        });
      } else {
        this.organisationService.getIntegrationCredentials('xero').subscribe((res) => {
          if (res) {
            this.organisationService.refreshConnectionURL = res.revokeEndPoint;
            this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, null,
              `Xero connection lost. Please <a  class=\"toastr-link\" href=\"${this.organisationService.refreshConnectionURL}\">refresh</a> connection`);
          }
        });
      }
    } else {
      let message = data.content + ` <a  class=\"toastr-link\" href=\"/inbox/editInvoice/${data?.data?.id}\">View Invoice</a> `
      this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, "Error", message);
    }
  }
}

